"use client";

import * as React from "react";
import { Plus } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuShortcut,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { Button } from "../ui/button";
import { useOrganization } from "../../lib/context/OrganisationContext";
import { ResponsiveDrawerForm } from "../ResponsiveDrawerForm";
import AddOrganizations from "../../pages/Organizations/AddOrganizations";
import { useNavigate, useParams } from "react-router-dom";
import { AnimatedTooltip } from "../AnimatedTooltip";

export function OrganizationSwitcher({ mobileMenu }) {
  const { organizationData, updateOrganizationId } = useOrganization();
  const [addOrganizationModal, setAddOrganizationModal] = React.useState(false);
  const navigate = useNavigate();
  const role = localStorage.getItem("role");
  const roleType = localStorage.getItem("roleType");
  const params = useParams();
  const trustId = params?.trust_id;

  const apperanceChange = (orgTypeSlug) => {
    if (orgTypeSlug === "real-estate-agent") {
      return "bg-[#0078C8]";
    } else if (orgTypeSlug === "auctioneer") {
      return "bg-[#084B83]";
    } else if (orgTypeSlug === "conveyancer" || orgTypeSlug === "lawyer") {
      return "bg-[#191970]";
    } else if (orgTypeSlug === "dealer") {
      return "bg-[#4BA3C7]";
    } else if (orgTypeSlug === "debt-collector") {
      return "bg-[#8f59a6]";
    } else if (orgTypeSlug === "general") {
      return "bg-[#254D70]";
    } else {
      return "bg-[#0078C8]";
    }
  };

  const [activeOrganization, setActiveOrganization] = React.useState(
    organizationData?.organizationId
      ? organizationData?.organizationList?.find(
          (item) => item.value === organizationData?.organizationId,
        )
      : organizationData?.organizationList?.[0],
  );

  React.useEffect(() => {
    setActiveOrganization(
      organizationData?.organizationId
        ? organizationData?.organizationList?.find(
            (item) => item.value === organizationData?.organizationId,
          )
        : organizationData?.organizationList?.[0],
    );
  }, [organizationData]);

  const toggleAddOrganizationModal = () => {
    setAddOrganizationModal(!addOrganizationModal);
  };

  return (
    <>
      <DropdownMenu>
        <AnimatedTooltip title={"Entity"} bottomAlign={true} leftAlign={true}>
          <DropdownMenuTrigger asChild>
            <Button
              size="md"
              className={`px-2 py-1.5 ${
                trustId
                  ? "bg-webSecondary-foreground text-black border"
                  : "bg-webSecondary text-white"
              } ${
                mobileMenu
                  ? "min-w-[246px] max-w-[246px]"
                  : "min-w-[200px] max-w-[200px]"
              } flex items-center gap-2 hover:text-white selection:bg-webPrimary selection:text-white`}
            >
              {/* Initial Icon/Avatar */}
              <div className="flex size-6 items-center justify-center rounded-sm border bg-webPrimary shrink-0">
                <div className="size-4 flex items-center justify-center text-white">
                  {activeOrganization?.label.charAt(0)}
                </div>
              </div>

              {/* Label with truncation */}
              <div className={`flex-1 min-w-0 text-left text-sm leading-tight`}>
                <div
                  title={activeOrganization?.label}
                  className="max-w-[144px] truncate whitespace-nowrap overflow-hidden font-semibold block"
                >
                  {activeOrganization?.label}
                </div>
              </div>
            </Button>
          </DropdownMenuTrigger>
        </AnimatedTooltip>
        <DropdownMenuContent
          className="w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg"
          align="start"
          side={"bottom"}
          sideOffset={4}
        >
          <DropdownMenuLabel className="text-xs text-muted-foreground mb-1">
            Entitys
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          {organizationData?.organizationList?.length > 0 &&
            organizationData?.organizationList?.map((org, index) => (
              <DropdownMenuItem
                key={index}
                onClick={() => {
                  setActiveOrganization(org);
                  updateOrganizationId(org?.value);
                  if (org?.is_subscribed) {
                    navigate("/trusts");
                  } else if (org?.is_aml_ctf_subscribed) {
                    navigate("/aml-ctf?tab=program");
                  } else {
                    navigate("/general-settings?tab=subscription");
                  }
                }}
                className={`gap-2 p-2 mb-1 ${
                  org?.value === organizationData?.organizationId
                    ? "bg-accent"
                    : ""
                }`}
              >
                <div
                  className={`flex size-6 items-center justify-center rounded-sm border ${apperanceChange(
                    org?.organization_type?.slug,
                  )} text-white`}
                >
                  <div className="size-4 shrink-0 flex items-center justify-center">
                    {org?.label.charAt(0)}
                  </div>
                </div>
                {org.label}
                <DropdownMenuShortcut>⌘{index + 1}</DropdownMenuShortcut>
              </DropdownMenuItem>
            ))}
          <DropdownMenuSeparator />
          {role === "Admin" && roleType === "Organization" && (
            <DropdownMenuItem
              className="gap-2 p-2"
              onClick={() => {
                toggleAddOrganizationModal();
              }}
            >
              <div className="flex size-6 items-center justify-center rounded-md border bg-background">
                <Plus className="size-4" />
              </div>
              <div className="font-medium text-muted-foreground">
                Add Entity
              </div>
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      <ResponsiveDrawerForm
        description={""}
        title={"Add Entity"}
        handleOpen={() => {}}
        handleClose={() => {
          setAddOrganizationModal(false);
        }}
        buttonText={""}
        className={""}
        open={addOrganizationModal}
        buttonClassName={""}
        setOpen={setAddOrganizationModal}
      >
        <AddOrganizations
          toggleAddOrganizationModal={toggleAddOrganizationModal}
        />
      </ResponsiveDrawerForm>
    </>
  );
}
